import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";
import { Reveal, Eyebrow } from "./Reveal";
import { CHAPTERS, IMAGES } from "../data/content";

function ParallaxImage({ src, alt, testId }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);
  return (
    <div ref={ref} className="overflow-hidden rounded-sm border border-border aspect-[4/3]">
      <motion.img
        src={src}
        alt={alt}
        style={{ scale }}
        className="w-full h-full object-cover"
        data-testid={testId}
      />
    </div>
  );
}

export default function Manifesto() {
  return (
    <section id="guides" data-testid="guides-section" className="relative py-24 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <Eyebrow data-testid="guides-eyebrow">The Manifesto</Eyebrow>
          <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-3xl leading-[1.08]" data-testid="guides-heading">
            Four chapters. Every decision an owner faces.
          </h2>
        </Reveal>

        <div className="mt-24 md:mt-40 space-y-32 md:space-y-48">
          {CHAPTERS.map((ch, i) => {
            const flip = i % 2 === 1;
            return (
              <article key={ch.number} data-testid={`chapter-${ch.number}`} className="relative">
                <span
                  aria-hidden
                  className={`font-serif text-outline select-none pointer-events-none absolute -top-[9vw] text-[26vw] md:text-[17vw] leading-none z-0 ${
                    flip ? "right-0" : "left-0"
                  }`}
                >
                  {ch.number}
                </span>
                <div className="relative z-10 grid md:grid-cols-12 gap-10 md:gap-16 items-center pt-[10vw] md:pt-[6vw]">
                  <Reveal className={`md:col-span-6 ${flip ? "md:order-2" : ""}`}>
                    <p className="uppercase tracking-[0.25em] text-xs font-bold text-muted-foreground font-sans">
                      Chapter {ch.number}
                    </p>
                    <h3 className="font-serif text-primary tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.12]">
                      {ch.title}
                    </h3>
                    <p className="mt-6 text-foreground/70 max-w-lg">{ch.lede}</p>
                    <ul className="mt-8 space-y-4 max-w-xl">
                      {(ch.points || []).map((p, j) => (
                        <li key={j} className="flex gap-3 text-sm md:text-base text-foreground/80">
                          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          {p}
                        </li>
                      ))}
                    </ul>
                    {ch.checklist && (
                      <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl" data-testid={`chapter-${ch.number}-checklist`}>
                        {ch.checklist.map((c, j) => (
                          <li key={j} className="flex items-start gap-3 bg-card border border-border rounded-sm px-4 py-3.5 text-sm font-medium text-foreground/80 hover:border-accent/50 transition-colors duration-300">
                            {ch.number === "04" ? (
                              <Warning size={18} weight="fill" className="text-accent shrink-0 mt-0.5" />
                            ) : (
                              <CheckCircle size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
                            )}
                            {c}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to={`/guides/${ch.slug}`}
                      data-testid={`chapter-${ch.number}-read-more`}
                      className="group inline-flex items-center gap-2 mt-10 text-sm font-semibold text-primary border-b-2 border-accent pb-1 hover:text-accent transition-colors duration-300"
                    >
                      Read the full chapter
                      <ArrowRight size={15} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Reveal>
                  {ch.image && (
                    <Reveal delay={0.15} className={`md:col-span-6 ${flip ? "md:order-1" : ""}`}>
                      <ParallaxImage src={IMAGES[ch.image]} alt={ch.title} testId={`chapter-${ch.number}-image`} />
                    </Reveal>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
